import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useEffectOnce, useLocalStorage } from 'react-use';

import Button from '../../../components/buttons/Button';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import ImageWithFallback from '../../../components/ImageWithFallback/ImageWithFallback';
import { AppLogo } from '../../../components/SVGIcons/AppLogo/AppLogo';
import { useLanderBanner } from '../../../hooks/useLanderBanner';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { DialogContent } from '../../../Payments/v2/src/components/DialogContent/DialogContent';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { emailValidator, stripNonAsciiPassword } from '../../../utils/validators';
import EmailAndPasswordInputs from '../EmailAndPasswordInputs/EmailAndPasswordInputs';
import SignUpConsents from '../SignUpConsents/SignUpConsents';
import { getEmailErrorMessageMap, getPasswordErrorMessageMap } from '../SignUpRegister/constants';
import { ThirdPartyAuth } from '../ThirdPartyAuth/ThirdPartyAuth';
import { useOpenAuthWizard } from '../useOpenAuthWizard';
import { useSignUpWizardContext } from '../wizards/signUpWizard.provider';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import classes from './SignUpEmailLanding.module.scss';

const SignUpEmailLanding = observer(() => {
  const { authStore, marketingConsentStore, landingStore } = useStores();
  const [_signUpMarketingConsentsLS, setSignUpMarketingConsentsLS] = useLocalStorage<Record<string, boolean>>(
    'signup-marketing-consents',
    {},
  );

  const [initializedMarketingConsents, setInitializedMarketingConsents] = useState(false);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const { t } = useTranslation();
  const { error, updateContextData } = useSignUpWizardContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const blockSignupWithEmailFeatureFlag = useFlag('block_signup_with_email', false);

  const [bypassBlockSignupWithEmail, setBypassBlockSignupWithEmail] = useState(false);
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');

  const clearError = () => updateContextData({ error: '' });

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;

  const {
    marketingConsents: marketingConsentsFromHook,
    onChangeMarketingConsent: onChangeMarketingConsentFromHook,
    areAllOptionalConsentsChecked,
  } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  const { isSigningUp } = authStore;
  const landingKey = authStore.getLandingKey();
  const { bannerUrl } = useLanderBanner('marketing');
  const { openAuthWizard } = useOpenAuthWizard();
  const { handleSignUpWizard } = useHandleSignUpWizard();

  const blockSignupWithEmail = blockSignupWithEmailFeatureFlag && !bypassBlockSignupWithEmail;

  const isApprovalFieldsValid = Boolean(isAgeConfirmed && isTermsAccepted);

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.data === 'bypassBlockSignupWithEmail') {
        setBypassBlockSignupWithEmail(true);
      }
    };

    window.addEventListener('message', eventHandler);

    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, []);

  useEffect(() => {
    if (!fetchingSignUpMarketingConsent) {
      setInitializedMarketingConsents(true);
    }
  }, [fetchingSignUpMarketingConsent]);

  useEffectOnce(() => {
    landingStore.sendLandingPageEvent('direct_em');
  });

  const handleLogin = () => {
    void analytics.sendEvent('login_account_button', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'log in',
    });

    openAuthWizard('logInWizard', {
      triggeredBy: 'sign_up_platform',
      force: true,
      persistent: true,
      landingKey: landingKey ?? undefined,
    });
  };

  const validateApprovalFields = () => {
    if (!isApprovalFieldsValid) {
      setApproveError(t('auth.approvalConditionsRequired'));
    }

    if (isApprovalFieldsValid) {
      setSignUpMarketingConsentsLS(marketingConsentsFromHook);
    }

    return isApprovalFieldsValid;
  };

  const handleEmailSignUp = () => {
    const emailError = emailValidator(email.trim());

    if (emailError || !password.trim()) {
      updateContextData({ error: t('auth.pleaseFillInAllRequiredFields') });
      return;
    }

    updateContextData({ error: '' });
    const isValid = validateApprovalFields();

    if (!isValid) {
      return;
    }

    handleSignUpWizard({ provider: 'email', credentials: { email, password }, areAllOptionalConsentsChecked });
  };

  const onChangeMarketingConsent = (checked: boolean, consentId: string) => {
    onChangeMarketingConsentFromHook(checked, consentId);
  };

  if (!initializedMarketingConsents) {
    return null;
  }

  return (
    <div className={classNames(classes.signUpEmailLanding)}>
      <AppLogo alwaysColorful withText className={classes.crownsHeader} textColor="#fff" width={48} />

      <DialogLoader isProcessing={isSigningUp} />

      <DialogContent className={classNames(classes.signUpEmailLandingContent)}>
        {bannerUrl && <ImageWithFallback alt="Landing" className={classes.landerBannerImg} src={bannerUrl} />}

        <EmailAndPasswordInputs
          disabled={isSigningUp}
          emailErrorMessageMap={getEmailErrorMessageMap(t)}
          password={password}
          passwordErrorMessageMap={getPasswordErrorMessageMap(t)}
          size={isMobile ? 'sm' : undefined}
          onChangeEmail={(newEmail) => {
            clearError();
            setEmail(newEmail);
          }}
          onChangePassword={(newPassword) => {
            clearError();
            setPassword(stripNonAsciiPassword(newPassword));
          }}
        />

        <SignUpConsents
          approveError={approveError}
          isAgeConfirmed={isAgeConfirmed}
          isTermsAccepted={isTermsAccepted}
          marketingConsents={marketingConsentsFromHook}
          setIsAgeConfirmed={setIsAgeConfirmed}
          setIsTermsAccepted={setIsTermsAccepted}
          signUpMarketingConsents={signUpMarketingConsents}
          onChangeMarketingConsent={onChangeMarketingConsent}
        />

        <div className={classes.bottomAuthOptions}>
          <ErrorMessage className={classes.authErrorMessage}>{error}</ErrorMessage>
          {!blockSignupWithEmail && (
            <>
              <Button
                btnType="main"
                className={classes.emailSignUpButton}
                dataTestId="signup-create-account-btn"
                disabled={!!error}
                onClick={handleEmailSignUp}
              >
                {t('auth.createAccountUpperCase')}
              </Button>
              <span className={classes.orText}>{t('common.or')}</span>
            </>
          )}
          <ThirdPartyAuth
            action="Sign Up"
            className={classes.thirdPartyAuth}
            excludedProviders={['facebook', 'apple']}
            onLoginAttempt={validateApprovalFields}
          />
        </div>

        <div
          className={classNames(classes.loginSectionMt, {
            'animation-slide-down': true,
            'form-links': true,
          })}
          data-testid="signup-email-already-have-account"
        >
          <Trans
            components={{
              question: <span />,
              login: <span className="text-link" onClick={handleLogin} />,
            }}
            i18nKey="auth.alreadyHaveAccountLogIn"
          />
        </div>
      </DialogContent>
    </div>
  );
});

export default SignUpEmailLanding;
